import React, { useCallback, useState } from 'react';
import { Pencil1Icon } from '@radix-ui/react-icons';
import { observer } from 'mobx-react-lite';
import { IconButton } from '@producer/ui-kit';

import { formatDate } from '../../../lib/utils/DateHelper';
import { StringFormat } from '../../../assets/contants/dates-fns';
import { DateSelectionModal } from '../../process/components/date-selection.modal';
import { InlineTimeSelector } from '../../schedule/components/InlineTimeSelector';
import { timeTo24HourFormat, timeTo12HourFormat } from '../../../lib/utils/Convertor';
import { fetchStripBoard } from '../../strip-board/strip-board.slice';
import { KeyboardKeys } from '../../../assets/enums/keyboard-keys.enum';
import { Step } from '../../../app/entities/step';
import { asPlainDate, isPlainDate, type PlainDate } from '../../../core/services/plain-date';

import styles from './InfoCard.module.scss';
type InfoCardProps = {
  step: Step;
  projectId: string;
  startDate?: PlainDate | null;
  dueDate?: PlainDate | null;
  generalCall: {
    hour: number;
    minute: number;
  };
};

export const InfoCard: React.FC<InfoCardProps> = observer(
  ({ step, projectId, startDate, dueDate, generalCall }) => {
    const [isEditDateOpen, setIsEditDateOpen] = useState<boolean>();

    const onSubmit = useCallback(
      async (value: InfoCardProps['generalCall']) => {
        const time = timeTo24HourFormat(value!);
        const { hour, minute } = time || {};

        await step.update({
          info: {
            generalCallTime: {
              hour,
              minute,
            },
          },
        });

        fetchStripBoard(projectId);
      },
      [projectId, step],
    );

    const handleOpenDate = () => {
      setIsEditDateOpen(true);
    };

    // `DateSelectionModal` hands back a raw `<input type="date">` value here — this component
    // uses the modal directly rather than through `DateSelector`, so it is its own boundary
    // where a real `PlainDate` is minted. Clearing is a real, API-supported action (the field is
    // nullable end to end), so an empty value is forwarded as `null` rather than swallowed — only
    // a non-empty, malformed value is dropped in place, since there is no safe `PlainDate` to
    // mint from it.
    const handleUpdateStep = async (value: string | null) => {
      if (!value) {
        step.update({ startDate: null, dueDate: null });
        return;
      }

      if (!isPlainDate(value)) return;

      const plainDate = asPlainDate(value);
      step.update({ startDate: plainDate, dueDate: plainDate });
    };

    const generalCallTime = generalCall && timeTo12HourFormat(generalCall);

    return (
      <div className={styles['container']}>
        <div className={styles['mobile-button-wrapper']}>
          <IconButton icon={<Pencil1Icon />} onClick={() => setIsEditDateOpen(true)} size="small" />
        </div>

        <div className={styles['time-container']}>
          <div className={styles['content']}>
            <InlineTimeSelector
              className={styles['time-display']}
              onChange={onSubmit}
              time={generalCallTime}
            />
          </div>
        </div>

        <button
          className={styles['footer']}
          tabIndex={0}
          onClick={handleOpenDate}
          onKeyDown={(e) => {
            if (e.key === KeyboardKeys.Enter) {
              handleOpenDate();
            }
          }}
        >
          {startDate || dueDate ? formatDate(startDate || dueDate!, StringFormat) : 'Choose date'}
        </button>

        {isEditDateOpen && (
          <DateSelectionModal
            onCancel={() => setIsEditDateOpen(false)}
            fieldName="startDate"
            value={startDate ?? undefined}
            onSubmit={handleUpdateStep}
          />
        )}
      </div>
    );
  },
);
